// ** React Imports
import { useContext, useCallback } from 'react'

// ** Next Imports

//** MUI Imports
import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'

// Icon Imports
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded'
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded'
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded'

// ** Third Party Imports
import { Step } from 'react-joyride'

// ** Axios

// ** Hook Imports
import { AuthContext } from 'src/context/AuthContext'
import useFetch from 'src/hooks/useFetch'
import { KeyedMutator } from 'swr'
import useSWRMutation from 'swr/mutation'

// ** Custom Imports
import myToast from 'src/components/myToast'
import CorrectionsTable from './CorrectionsTable'

// ** Config
import endPoint from 'src/configs/endpoints/riwayatHidupEndPoints'

// ** Type
import { CekRiwayatHidupJSON } from 'src/types/pelamar-identitas'

// Status indicator component to reduce duplication
const StatusIndicator = ({ status }: { status: string }) => {
  if (status === 'new') {
    return (
      <>
        <ErrorOutlineRoundedIcon color='error' sx={{ width: 16, height: 16, mr: 0.5 }} />
        <Typography variant='caption'>Perlu Anda perbaiki</Typography>
      </>
    )
  } else if (status === 'pending') {
    return (
      <>
        <AccessTimeRoundedIcon color='info' sx={{ width: 16, height: 16, mr: 0.5 }} />
        <Typography variant='caption'>Menunggu konfirmasi Panitia</Typography>
      </>
    )
  } else {
    return (
      <>
        <TaskAltRoundedIcon color='success' sx={{ width: 16, height: 16, mr: 0.5 }} />
        <Typography variant='caption'>Koreksi Benar</Typography>
      </>
    )
  }
}

// Status legend reusable component
const StatusLegend = () => {
  return (
    <Box
      sx={{
        mt: 4,
        mx: 4,
        px: 4,
        py: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        borderRadius: 1,
        backgroundColor: theme => theme.myColor?.colorToggleLight
      }}
    >
      <Typography variant='subtitle2' sx={{ fontWeight: 500 }}>
        Keterangan Status:
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'left', alignItems: 'center' }}>
        <ErrorOutlineRoundedIcon color='error' sx={{ width: 16, height: 16, mr: 1 }} />
        <Typography variant='caption'>Perlu diperbaiki</Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'left', alignItems: 'center' }}>
        <AccessTimeRoundedIcon color='info' sx={{ width: 16, height: 16, mr: 1 }} />
        <Typography variant='caption'>Menunggu konfirmasi Panitia</Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'left', alignItems: 'center' }}>
        <TaskAltRoundedIcon color='success' sx={{ width: 16, height: 16, mr: 1 }} />
        <Typography variant='caption'>Koreksi Benar</Typography>
      </Box>
    </Box>
  )
}

const CustomodalCorrection = ({
  openModal,
  setOpenModal,
  setCorectionStart,
  setSteps,
  cekRiwayatHidupJSON,
  mutateIdentitasPelamar
}: {
  openModal: boolean
  setOpenModal: (open: boolean) => void
  setCorectionStart: (open: boolean) => void
  setSteps: (steps: Step[]) => void
  cekRiwayatHidupJSON: CekRiwayatHidupJSON[]
  mutateIdentitasPelamar: KeyedMutator<any>
}) => {
  // ** Hooks
  const { ReqWithPayload } = useFetch()
  const auth = useContext(AuthContext)

  const { trigger } = useSWRMutation(`${process.env.NEXT_PUBLIC_BASE_PATH}/api/reqHandler/`, ReqWithPayload)

  // Fix: use openModal instead of undefined 'open' variable
  const handleClose = useCallback(() => {
    setOpenModal(false)
  }, [setOpenModal])

  const handleStartCorection = useCallback(
    (row: CekRiwayatHidupJSON) => {
      const step: Step = {
        data: { cekRiwayatHidupJSON, row, mutateIdentitasPelamar, setOpenModal },
        title: row.title,
        content: (
          <Box>
            <Box sx={{ px: 8, mb: 2, mt: 4 }}>{row.content}</Box>
            <Box sx={{ my: 4, mx: 8, display: 'flex', justifyContent: 'left', alignItems: 'center' }}>
              <StatusIndicator status={row.status} />
            </Box>
          </Box>
        ),
        placement: 'auto',
        target: row.target,
        disableBeacon: true
      }

      setSteps([step])
      setCorectionStart(true)
      handleClose()
    },
    [cekRiwayatHidupJSON, mutateIdentitasPelamar, setOpenModal, setSteps, setCorectionStart, handleClose]
  )

  const handleSayaSudahPerbaiki = useCallback(
    (row: CekRiwayatHidupJSON) => {
      const newCekRiwayatHidupJSON = cekRiwayatHidupJSON.map(item =>
        item.target === row.target ? { ...item, status: 'pending' } : item
      )

      const payload = {
        url: `${endPoint.pelamarSetCekRiwayatHidupEndpoint}/${auth.user?.id}`,
        method: 'PATCH',
        body: newCekRiwayatHidupJSON
      }

      const myPromise = new Promise<string>((resolve, reject) => {
        trigger(payload, {
          onSuccess: data => {
            if (data.success) {
              resolve('OK')
              mutateIdentitasPelamar()
            } else {
              reject(data.message ? data.message : 'Unknown Error')
            }
          },
          onError: () => reject('Unknown Error')
        })
      })

      myToast(myPromise, 'Simpan Data Sukses!', undefined, 'Simpan Data Gagal!')
    },
    [auth.user?.id, cekRiwayatHidupJSON, mutateIdentitasPelamar, trigger]
  )

  return (
    <Dialog
      onClose={handleClose}
      open={openModal}
      fullWidth={true}
      maxWidth='md'
      sx={{
        '& .MuiPaper-root': {
          p: 4,
          pt: 8
        }
      }}
    >
      <IconButton
        aria-label='close'
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 4,
          top: 4,
          p: 0,
          color: theme => theme.palette.error.main
        }}
      >
        <HighlightOffRoundedIcon />
      </IconButton>
      <Box>
        <Typography
          variant='h6'
          textAlign='center'
          sx={{
            mb: 4,
            fontWeight: 600,
            color: theme => theme.myColor?.colorToggle
          }}
        >
          Daftar Koreksi Anda
        </Typography>
        <Box sx={{ px: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Typography variant='subtitle2' sx={{ fontWeight: 400 }}>
            Harap segera revisi riwayat hidup Anda, khususnya pada bagian-bagian yang tercantum di bawah ini, sebelum
            tanggal{' '}
            <Box component='span' sx={{ fontWeight: 600, color: theme => theme.myColor?.colorToggle }}>
              28 Juli 2024
            </Box>
            .
          </Typography>
          <Typography variant='subtitle2' sx={{ fontWeight: 400 }}>
            Setelah Anda selesai revisi, mohon klik tombol{' '}
            <Box
              component='span'
              sx={{
                fontWeight: 600,
                color: theme => theme.palette.success.main
              }}
            >
              <DoneAllRoundedIcon sx={{ mb: -0.6, mr: 1, width: 16, height: 16 }} />
              Sudah diperbaiki
            </Box>{' '}
            agar panitia dapat melakukan pemeriksaan.
          </Typography>
          <Typography variant='subtitle2' sx={{ fontWeight: 400 }}>
            Terima kasih atas perhatian dan kerjasamanya.
          </Typography>
        </Box>

        <CorrectionsTable
          data={cekRiwayatHidupJSON}
          onViewClick={handleStartCorection}
          onFixClick={handleSayaSudahPerbaiki}
        />
        <StatusLegend />
      </Box>
    </Dialog>
  )
}

export { StatusLegend }
export default CustomodalCorrection
